import type { ApiTokenDisplay, AdminStats, AdminRecruiter, AlarmStatus } from 'aliv-shared';

const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3000';

let tokenProvider: (() => Promise<string>) | null = null;

export function setAuthTokenProvider(provider: () => Promise<string>) {
  tokenProvider = provider;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...Object.fromEntries(
      Object.entries(init?.headers ?? {}).filter(([, v]) => typeof v === 'string') as [
        string,
        string,
      ][],
    ),
  };

  if (tokenProvider) {
    try {
      const token = await tokenProvider();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      console.error('Failed to retrieve auth token, proceeding without auth:', err);
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  const data = await res.json();

  if (!res.ok) {
    throw new Error((data as { message?: string }).message ?? `HTTP ${res.status}`);
  }

  return data as T;
}

export interface CreateSessionPayload {
  candidateName: string;
  recruiterPhone?: string;
  candidateEmail?: string;
  confidenceThreshold?: number;
}

export interface CreateSessionResponse {
  sessionId: string;
  checkUrl: string;
  expiresAt: string;
}

export interface SessionResult {
  sessionId: string;
  candidateName: string;
  candidateProfileUrl?: string;
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'EXPIRED';
  confidence?: number;
  passed?: boolean;
  checkUrl: string;
  createdAt: string;
  completedAt?: string;
}

export interface CompleteSessionResponse {
  status: 'SUCCEEDED' | 'FAILED' | 'EXPIRED';
  confidence?: number;
  passed: boolean;
}

export const api = {
  createSession: (payload: CreateSessionPayload) =>
    request<CreateSessionResponse>('/sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getSession: (sessionId: string) => request<SessionResult>(`/sessions/${sessionId}`),

  listSessions: (limit?: number, cursor?: string) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    const qs = params.toString();
    return request<{ sessions: SessionResult[]; nextCursor?: string }>(
      `/sessions${qs ? `?${qs}` : ''}`,
    );
  },

  completeSession: (sessionId: string) =>
    request<CompleteSessionResponse>(`/sessions/${sessionId}/complete`, {
      method: 'POST',
    }),

  deleteSession: (sessionId: string) =>
    request<{ deleted: boolean }>(`/sessions/${sessionId}`, {
      method: 'DELETE',
    }),

  deleteAccount: () =>
    request<{ deleted: boolean }>('/account', {
      method: 'DELETE',
    }),

  createApiToken: (displayName: string) =>
    request<{ token: string; displayName: string }>('/api-tokens', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    }),

  listApiTokens: () => request<{ tokens: ApiTokenDisplay[] }>('/api-tokens'),

  deleteApiToken: (tokenId: string) =>
    request<{ deleted: boolean }>(`/api-tokens/${tokenId}`, {
      method: 'DELETE',
    }),

  adminStats: () => request<AdminStats>('/admin/stats'),

  adminListSessions: (limit = 50, cursor?: string, status?: string) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    if (status) params.set('status', status);
    return request<{ sessions: SessionResult[]; nextCursor?: string }>(`/admin/sessions?${params}`);
  },

  adminListRecruiters: () => request<{ recruiters: AdminRecruiter[] }>('/admin/recruiters'),

  adminGetRecruiter: (recruiterId: string) =>
    request<{ sessions: SessionResult[] }>(`/admin/recruiters/${recruiterId}`),

  adminToggleRecruiter: (recruiterId: string, enabled: boolean) =>
    request<{ success: boolean }>(`/admin/recruiters/${recruiterId}/toggle`, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }),

  adminHealth: () =>
    request<{ alarms: AlarmStatus[]; metrics: Record<string, number> }>('/admin/health'),
};
